import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  getProductionPeriods,
  getProductionPeriodById,
  postProductionPeriod,
  updateProductionPeriodById,
  deleteProductionPeriodById,
} from './production-period.thunk';
import type {
  ProductionPeriod,
  ProductionPeriodListResponse,
} from '../utils/types';
import type {
  CreateProductionPeriodResponse,
  UpdateProductionPeriodResponse,
  DeleteProductionPeriodResponse,
} from '../api/fetch-production-periods.api';

interface ProductionPeriodState {
  productionPeriodListLoading: boolean;
  productionPeriodData?: ProductionPeriodListResponse;
  error?: string | null;
  productionPeriodDetailLoading: boolean;
  productionPeriodDetailData?: ProductionPeriod;
  detailError?: string | null;
  creating: boolean;
  createProductionPeriodLoading: boolean;
  createError?: string | null;
  createResult?: CreateProductionPeriodResponse;
  updateProductionPeriodLoading: boolean;
  updateError?: string | null;
  updateResult?: UpdateProductionPeriodResponse;
  deleteProductionPeriodLoading: boolean;
  deleteError?: string | null;
  deleteResult?: DeleteProductionPeriodResponse;
}

const initialState: ProductionPeriodState = {
  productionPeriodListLoading: false,
  createProductionPeriodLoading: false,
  productionPeriodData: undefined,
  error: null,
  productionPeriodDetailLoading: false,
  productionPeriodDetailData: undefined,
  detailError: null,
  creating: false,
  createError: null,
  createResult: undefined,
  updateProductionPeriodLoading: false,
  updateError: null,
  updateResult: undefined,
  deleteProductionPeriodLoading: false,
  deleteError: null,
  deleteResult: undefined,
};

const productionPeriodSlice = createSlice({
  name: 'productionPeriod',
  initialState,
  reducers: {
    resetCreateState(state) {
      state.creating = false;
      state.createError = null;
      state.createResult = undefined;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getProductionPeriods.pending, state => {
        state.productionPeriodListLoading = true;
        state.error = null;
      })
      .addCase(
        getProductionPeriods.fulfilled,
        (state, action: PayloadAction<ProductionPeriodListResponse>) => {
          state.productionPeriodData = action.payload;
          state.productionPeriodListLoading = false;
        }
      )
      .addCase(getProductionPeriods.rejected, (state, action) => {
        state.productionPeriodListLoading = false;
        state.error =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch production periods';
      })
      .addCase(getProductionPeriodById.pending, state => {
        state.productionPeriodDetailLoading = true;
        state.detailError = null;
      })
      .addCase(
        getProductionPeriodById.fulfilled,
        (state, action: PayloadAction<ProductionPeriod>) => {
          state.productionPeriodDetailLoading = false;
          state.productionPeriodDetailData = action.payload;
        }
      )
      .addCase(getProductionPeriodById.rejected, (state, action) => {
        state.productionPeriodDetailLoading = false;
        state.detailError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to fetch production period';
      })
      .addCase(postProductionPeriod.pending, state => {
        state.createProductionPeriodLoading = true;
        state.createError = null;
        state.createResult = undefined;
      })
      .addCase(
        postProductionPeriod.fulfilled,
        (state, action: PayloadAction<CreateProductionPeriodResponse>) => {
          state.createProductionPeriodLoading = false;
          state.createResult = action.payload;
        }
      )
      .addCase(postProductionPeriod.rejected, (state, action) => {
        state.createProductionPeriodLoading = false;
        state.createError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to create production period';
      })
      .addCase(updateProductionPeriodById.pending, state => {
        state.updateProductionPeriodLoading = true;
        state.updateError = null;
        state.updateResult = undefined;
      })
      .addCase(
        updateProductionPeriodById.fulfilled,
        (state, action: PayloadAction<UpdateProductionPeriodResponse>) => {
          state.updateProductionPeriodLoading = false;
          state.updateResult = action.payload;
        }
      )
      .addCase(updateProductionPeriodById.rejected, (state, action) => {
        state.updateProductionPeriodLoading = false;
        state.updateError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to update production period';
      })
      .addCase(deleteProductionPeriodById.pending, state => {
        state.deleteProductionPeriodLoading = true;
        state.deleteError = null;
        state.deleteResult = undefined;
      })
      .addCase(
        deleteProductionPeriodById.fulfilled,
        (state, action: PayloadAction<DeleteProductionPeriodResponse>) => {
          state.deleteProductionPeriodLoading = false;
          state.deleteResult = action.payload;

          if (!state.productionPeriodData) return;

          const nextItems = state.productionPeriodData.items.filter(
            item => item.id !== action.payload.id
          );

          state.productionPeriodData.items = nextItems;
          state.productionPeriodData.total = nextItems.length;
        }
      )
      .addCase(deleteProductionPeriodById.rejected, (state, action) => {
        state.deleteProductionPeriodLoading = false;
        state.deleteError =
          (action.payload as string | undefined) ??
          action.error.message ??
          'Failed to delete production period';
      });
  },
});

export const { resetCreateState } = productionPeriodSlice.actions;
export default productionPeriodSlice.reducer;
